import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { WarningCircle, X } from '@phosphor-icons/react';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText: string;
  isLoading?: boolean;
  error: string | null;
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  isLoading = false,
  error
}: ConfirmationDialogProps) {
  return (
    <DialogRoot
      role="alertdialog"
      placement="center"
      size="lg"
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
    >
      <DialogContent borderRadius="12px"> 
        <DialogHeader>
          <DialogTitle>
            <Flex alignItems="center" gap="8px">
              <WarningCircle size={32} color="#4E76FF" />
              <Text fontSize="24px" fontWeight="bold">
                {title}
              </Text>
            </Flex>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box borderBottom="1px solid" borderColor="neutrals.100" mb="24px" />
          <Text fontSize="16px" px="16px" color="neutrals.700">
            {description}
          </Text>
          {error && (
            <Text fontSize="16px" mt="24px" px="16px" color="error.500">
              {error}
            </Text>
          )}
        </DialogBody>
        <DialogFooter gap="12px">
          <DialogActionTrigger asChild>
            <Button
              bg="neutrals.0"
              w="120px"
              fontSize="16px"
              borderRadius="8px"
              borderColor="primary.900"
              variant="outline"
              color="primary.900"
              _hover={{ bg: 'primary.50' }}
              disabled={isLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            bg="primary.900"
            w="120px"
            fontSize="16px"
            borderRadius="8px"
            onClick={onConfirm}
            loading={isLoading}
            _hover={{ bg: 'primary.700' }}
          >
            {confirmText}
          </Button>
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={isLoading}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
